/* eslint-disable camelcase */
/* eslint-disable react/display-name */
/**
 * @file VaccineSchedulingView.jsx
 * @author Daniel Aguirre
 * @description Recommended By Scheduling View Page
 */

// Dependencies
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import DashboardCard from 'components/DashboardCard';
import DashboardBar from 'components/Charts/DashboardBar';
import { getMonthShortName } from 'commons/helpers/utils';
import styleGuideColors from 'commons/styleGuideColors';
import { useHistory } from 'react-router';

/**
 * VaccineScheduling View
 * @param { Object } dashboardStaffActions - DashboardStaffActions Actions.
 * @return { Node } React node.
 */

function VaccineSchedulingView({ dashboardStaffActions, clinicId }) {
  const history = useHistory();
  const [chartData, setChartData] = useState(null);
  const { t: translate } = useTranslation();

  const { getVaccineScheduling } = dashboardStaffActions;

  const onGetData = (_, resp) => {
    let labels = [];
    let dates = [];
    let months = [];
    let years = [];

    const datasets = [
      {
        label: translate('dashboardStaff.cuantos_dosis2'),
        classification: 'dosis 2',
        data: [],
        backgroundColor: styleGuideColors.baxterBrandTeal
      },
      {
        label: translate('dashboardStaff.cuantos_dosis3'),
        classification: 'dosis 3',
        data: [],
        backgroundColor: styleGuideColors.baxterBrandBaxterBlue
      },
      {
        label: translate('dashboardStaff.cuantos_vencidos'),
        classification: 'vencidos',
        data: [],
        backgroundColor: styleGuideColors.alertError
      }
    ];

    if (resp?.data) {
      resp.data.forEach(
        ({ cuantos_dosis2, cuantos_dosis3, cuantos_vencidos, mes_vacuna }) => {
          const dateSplit = mes_vacuna?.split('-');
          if (dateSplit) {
            const [year, month] = dateSplit;
            const date = `${year}-${month}`;
            if (dates.indexOf(date) === -1) {
              dates = [...dates, date];
              const shortMonth = getMonthShortName(Number(month) - 1);
              labels = [...labels, translate(shortMonth)];
              years = [...years, year];
              months = [...months, month];
            }
            const [dose2, dose3, expired] = datasets;
            dose2.data = [...dose2.data, cuantos_dosis2];
            dose3.data = [...dose3.data, cuantos_dosis3];
            expired.data = [...expired.data, cuantos_vencidos];
          }
        }
      );

      setChartData({
        labels,
        datasets,
        years,
        months
      });
    } else {
      setChartData([]);
    }
  };

  useEffect(() => {
    getVaccineScheduling({ clinicId }, onGetData);
  }, [clinicId, translate]);

  const handleDetailClick = (index, datasetIndex) => {
    const month = chartData.months[index];
    const year = chartData.years[index];
    const vaccineMonth = `${year}-${month}-01`;
    const totalItems = chartData.datasets[datasetIndex].data[index];
    const { classification: dose } = chartData.datasets[datasetIndex];
    history.push(
      `/main/dashboard-detail/vaccine-scheduling/${vaccineMonth}/${dose}/${totalItems}`
    );
  };

  return (
    <DashboardCard id="F9" loading={!chartData}>
      <DashboardBar
        title={translate('dashboardStaff.vaccinationScheduling')}
        description={translate('dashboardStaff.patientsScheduleVaccination')}
        yTitle={translate('dashboardStaff.numberOfPatients')}
        dataValues={chartData}
        onDetailClick={handleDetailClick}
      />
    </DashboardCard>
  );
}

/**
 * Component definition of proptypes.
 */
VaccineSchedulingView.propTypes = {
  dashboardStaffActions: PropTypes.shape({
    getVaccineScheduling: PropTypes.func.isRequired
  }),
  clinicId: PropTypes.any
};

export default VaccineSchedulingView;
